import { Button, Card, IconButton, Input, Textarea, Toggle } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type AgentSummary, listAgentsOptions } from "@/api";
import { Text } from "@/components/Text";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { collectSubtree, type StepNode, type StepNodeData } from "./flowEditorModel";

/** Right-panel agent picker (a step gets an agent, or stays a prompt step), searched by name on the server. */
export function AgentPicker({
  onAssign,
  onClose,
}: Readonly<{ onAssign: (a: AgentSummary) => void; onClose: () => void }>) {
  const { t } = useTranslation("workflows");
  const [search, setSearch] = useState("");
  const term = useDebouncedValue(search.trim());
  const agents = useQuery({
    ...listAgentsOptions({ query: { page_size: 100, search: term || undefined } }),
    placeholderData: keepPreviousData,
  });
  const list = agents.data?.data ?? [];
  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center justify-between">
        <Text variant="body">{t("editor.assignAgent")}</Text>
        <IconButton icon={<X />} aria-label={t("editor.cancel")} size="sm" onClick={onClose} />
      </div>
      <div>
        <Input
          leadingIcon={<Search />}
          placeholder={t("editor.searchAgents")}
          aria-label={t("editor.searchAgents")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {agents.isPending && (
        <Text variant="body-sm" tone="tertiary">
          {t("editor.assignAgent")}…
        </Text>
      )}
      {!agents.isPending && list.length === 0 && (
        <Text variant="body-sm" tone="tertiary">
          {term ? t("editor.noAgentsMatch", { term }) : t("editor.noAgents")}
        </Text>
      )}
      {list.length > 0 && (
        <ul className="-m-1 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-1">
          {list.map((a) => (
            <li key={a.uuid}>
              <Card onClick={() => onAssign(a)} size="sm" aria-label={a.name}>
                <Text variant="body-sm">{a.name}</Text>
                {a.description && (
                  <Text variant="caption" tone="tertiary" className="line-clamp-2">
                    {a.description}
                  </Text>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Right-panel step configuration: name, parent, agent, instructions, review. */
export function StepConfig({
  node,
  parentMap,
  nodes,
  onChange,
  onChangeParent,
  onPickAgent,
  onClearAgent,
  onDelete,
  onClose,
}: Readonly<{
  node: StepNode;
  parentMap: Map<string, string | null>;
  nodes: StepNode[];
  onChange: (patch: Partial<StepNodeData>) => void;
  onChangeParent: (parentId: string | null) => void;
  onPickAgent: () => void;
  onClearAgent: () => void;
  onDelete: () => void;
  onClose: () => void;
}>) {
  const { t } = useTranslation("workflows");
  const descendants = collectSubtree(node.id, parentMap);
  const parentOptions = nodes.filter((n) => n.id !== node.id && !descendants.has(n.id));
  const config = node.data.config ?? {};
  const prompt = typeof config.prompt === "string" ? config.prompt : "";
  const expectedOutput = typeof config.expected_output === "string" ? config.expected_output : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Text variant="body">{t("editor.stepConfig")}</Text>
        <IconButton icon={<X />} aria-label={t("editor.cancel")} size="sm" onClick={onClose} />
      </div>
      <Input
        aria-label={t("editor.stepName")}
        value={node.data.label}
        onChange={(e) => onChange({ label: e.target.value })}
      />
      <div className="flex flex-col gap-1">
        <Text variant="caption" tone="secondary">
          {t("editor.connectedFrom")}
        </Text>
        <select
          aria-label={t("editor.connectedFrom")}
          className="rounded-lg border border-border-input bg-surface-elevated px-3 py-2 text-content-primary"
          value={parentMap.get(node.id) ?? "__none__"}
          onChange={(e) => onChangeParent(e.target.value === "__none__" ? null : e.target.value)}
        >
          <option value="__none__">{t("editor.rootStep")}</option>
          {parentOptions.map((n) => (
            <option key={n.id} value={n.id}>
              {n.data.label || n.data.agentName}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Text variant="caption" tone="secondary">
          {t("editor.agent")} ({t("editor.agentOptional")})
        </Text>
        {node.data.agentName ? (
          <div className="flex items-center justify-between rounded-lg border border-border-input bg-surface-base px-3 py-2">
            <Text variant="body-sm">{node.data.agentName}</Text>
            <IconButton icon={<X />} aria-label={t("editor.removeAgent")} size="xs" onClick={onClearAgent} />
          </div>
        ) : (
          <Button variant="secondary" onClick={onPickAgent}>
            {t("editor.assignAnAgent")}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <Text variant="caption" tone="secondary">
          {t("editor.instructions")}
        </Text>
        <Textarea
          aria-label={t("editor.instructions")}
          rows={4}
          placeholder={t("editor.instructionsPlaceholder")}
          value={prompt}
          onChange={(e) => onChange({ config: { ...config, prompt: e.target.value } })}
        />
      </div>
      <Toggle
        label={t("editor.expectedOutput")}
        checked={expectedOutput != null}
        onChange={(on) => onChange({ config: { ...config, expected_output: on ? "" : undefined } })}
      />
      {expectedOutput != null && (
        <Textarea
          aria-label={t("editor.expectedOutput")}
          rows={3}
          placeholder={t("editor.expectedOutputPlaceholder")}
          value={expectedOutput}
          onChange={(e) => onChange({ config: { ...config, expected_output: e.target.value } })}
        />
      )}
      <Toggle
        label={t("editor.requiresReview")}
        checked={node.data.requiresReview}
        onChange={(on) => onChange({ requiresReview: on })}
      />
      {node.data.requiresReview && (
        <Text variant="caption" tone="tertiary">
          {t("editor.reviewNote")}
        </Text>
      )}
      <div className="border-t border-border-subtle pt-2">
        <Button variant="danger" onClick={onDelete}>
          {t("editor.removeStep")}
        </Button>
      </div>
    </div>
  );
}
